import React from 'react';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronRight } from 'lucide-react';
import cn from './cn';

/**
 * Radix DropdownMenu: what the kebab row-action menu is built on (`../RowActions.js`), and
 * the top bar's breadcrumb and account menus, whose Chapter and Subtenant pickers are the
 * submenus below.
 *
 * Radix owns the parts that are easy to get wrong: focus moves into the menu on open and
 * back to the trigger on close, typeahead works, Escape and outside clicks close it, and
 * it renders in a portal so a table's `overflow: auto` cannot clip it.
 *
 * The defaults here are the picker's. `RowActions` passes its own width, layer, row height
 * and colours through `className`, and `cn()` resolves the overlap.
 */
export const DropdownMenu = DropdownPrimitive.Root;
export const DropdownMenuTrigger = DropdownPrimitive.Trigger;
export const DropdownMenuGroup = DropdownPrimitive.Group;

/* The panel, shared by a menu and its submenus so the two read as one surface. */
const PANEL = cn(
  'tw-z-dialog tw-min-w-44 tw-rounded-card tw-p-1.5',
  'tw-border tw-border-solid tw-border-line-light tw-bg-white tw-shadow-lg',
  'tw-font-sans tw-text-body tw-animate-cms-fade-in focus:tw-outline-none'
);

/* A row, shared by an item and a submenu's trigger. */
const ROW = cn(
  'tw-flex tw-items-center tw-gap-2.5 tw-rounded-control tw-px-2.5 tw-py-1.5',
  'tw-cursor-pointer tw-outline-none tw-select-none',
  'data-[disabled]:tw-opacity-50 data-[disabled]:tw-pointer-events-none'
);

export const DropdownMenuContent = React.forwardRef(
  ({ className, align = 'end', sideOffset = 6, ...props }, ref) => (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(PANEL, className)}
        {...props}
      />
    </DropdownPrimitive.Portal>
  )
);
DropdownMenuContent.displayName = 'DropdownMenuContent';

/** `destructive` is always last and always after a separator, per the handoff. */
export const DropdownMenuItem = React.forwardRef(
  ({ className, destructive = false, ...props }, ref) => (
    <DropdownPrimitive.Item
      ref={ref}
      className={cn(
        ROW,
        destructive
          ? 'tw-text-danger data-[highlighted]:tw-bg-danger-tint'
          : 'tw-text-ink-2 data-[highlighted]:tw-bg-fill',
        className
      )}
      {...props}
    />
  )
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

/**
 * A submenu: a row that opens a second panel beside the first, with a chevron to say so.
 * Radix moves focus into it with the right arrow and back with the left, and keeps the row
 * lit while its panel is open.
 */
export const DropdownMenuSub = DropdownPrimitive.Sub;

export const DropdownMenuSubTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <DropdownPrimitive.SubTrigger
    ref={ref}
    className={cn(
      ROW,
      'tw-text-ink-2 data-[highlighted]:tw-bg-fill data-[state=open]:tw-bg-fill',
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="tw-ml-auto tw-shrink-0 tw-text-muted" size={16} aria-hidden="true" />
  </DropdownPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

export const DropdownMenuSubContent = React.forwardRef(
  ({ className, sideOffset = 8, ...props }, ref) => (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.SubContent
        ref={ref}
        sideOffset={sideOffset}
        className={cn(PANEL, className)}
        {...props}
      />
    </DropdownPrimitive.Portal>
  )
);
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

export const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownPrimitive.Separator
    ref={ref}
    className={cn('tw--mx-1.5 tw-my-1.5 tw-h-px tw-bg-line-light', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

export const DropdownMenuLabel = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownPrimitive.Label
    ref={ref}
    className={cn('tw-px-2.5 tw-py-1.5 tw-text-caption tw-font-bold tw-text-muted', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

export default DropdownMenu;
